import React, { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { importTeam } from '../store/teamSlice';
import { exportTeam, readTeamImportFile } from '../utils/teamExportImport';
import './TeamExportImport.css';

const TeamExportImport: React.FC = () => {
  const dispatch = useDispatch();
  const team = useSelector((state: RootState) => state.team.team);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<boolean>(false);

  // Handle export button click
  const handleExport = () => {
    try {
      exportTeam(team);
      // No need to show success message for export since file download is feedback enough
    } catch (error) {
      // If exportTeam throws an error, it's already logged in the function
      alert('Error exporting team. Please try again.');
    }
  };

  // Handle import button click - trigger file input
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Reset states
    setImportError(null);
    setImportSuccess(false);
    
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    try {
      const importedTeam = await readTeamImportFile(file);
      dispatch(importTeam(importedTeam));
      setImportSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setImportSuccess(false);
      }, 3000);
    } catch (error) {
      setImportError((error as Error).message);
    }
    
    // Reset file input
    e.target.value = '';
  };

  return (
    <div className="team-export-import">
      <div className="export-import-buttons">
        <button 
          className="export-button" 
          onClick={handleExport}
          title="Export team as JSON file"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
            <path d="M12 16L12 8M12 8L15 11M12 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 15L3 16C3 18.2091 4.79086 20 7 20L17 20C19.2091 20 21 18.2091 21 16L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Export Team
        </button>
        
        <button 
          className="import-button" 
          onClick={handleImportClick}
          title="Import team from JSON file"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
            <path d="M12 8V16M12 16L9 13M12 16L15 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Import Team
        </button>
        
        <input 
          ref={fileInputRef}
          type="file" 
          accept="application/json" 
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
      
      {importError && (
        <div className="import-error">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="15" r="1" fill="currentColor"/>
          </svg>
          {importError}
        </div>
      )}
      
      {importSuccess && (
        <div className="import-success">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Team imported successfully!
        </div>
      )}
    </div>
  );
};

export default TeamExportImport; 